import React, { FC } from "react";
import {
  Box,
  Typography,
  FormControlLabel,
  Checkbox,
  FormGroup,
  Tooltip,
} from "@mui/material";
import { InfoOutlined } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

import { FivePointScale } from "./FivePointScale";
import type { OpportunityAnswers } from "./DMSurveyInternal";

interface OpportunityAssessmentProps {
  currentAnswer: OpportunityAnswers | undefined;
  onUpdate: (field: string, value: unknown) => void;
  bgColor: string;
  ranges: {
    likelihood: string[];
    severity: string[];
    timeHorizon: string[];
  };
}

export const OpportunityAssessment: FC<OpportunityAssessmentProps> = ({
  currentAnswer = {},
  onUpdate,
  bgColor,
  ranges,
}) => {
  const { t } = useTranslation();

  return (
    <>
      {/* Likelihood */}
      <Box sx={{ bgcolor: bgColor, p: 3, borderRadius: 1 }}>
        <Typography variant="subtitle2" fontWeight="normal" sx={{ mb: 2 }}>
          {t(
            "dmSurvey.assessFinancialOpportunityLikelihood",
            "Assess the likelihood of the financial opportunity:",
          )}
          <Tooltip
            title={t(
              "dmSurvey.assessFinancialOpportunityLikelihoodTooltip",
              "Estimate the probability that this financial opportunity will materialize.",
            )}
          >
            <InfoOutlined
              sx={{
                fontSize: 18,
                color: "text.secondary",
                ml: 1,
                verticalAlign: "middle",
              }}
            />
          </Tooltip>
        </Typography>
        <FivePointScale
          value={currentAnswer.probability?.toString() || ""}
          onChange={(value) => onUpdate("probability", parseInt(value, 10))}
          labels={ranges.likelihood}
        />
      </Box>

      {/* Magnitude */}
      <Box sx={{ bgcolor: bgColor, p: 3, borderRadius: 1 }}>
        <Typography variant="subtitle2" fontWeight="normal" sx={{ mb: 2 }}>
          {t(
            "dmSurvey.assessPotentialMagnitudeOpportunity",
            "Assess the potential magnitude of financial opportunity:",
          )}
          <Tooltip
            title={t(
              "dmSurvey.assessPotentialMagnitudeOpportunityTooltip",
              "Estimate the potential financial gain or positive effect on the company's position.",
            )}
          >
            <InfoOutlined
              sx={{
                fontSize: 18,
                color: "text.secondary",
                ml: 1,
                verticalAlign: "middle",
              }}
            />
          </Tooltip>
        </Typography>
        <FivePointScale
          value={currentAnswer.levelOfSeverity?.toString() || ""}
          onChange={(value) => onUpdate("levelOfSeverity", parseInt(value, 10))}
          labels={ranges.severity}
        />
      </Box>

      {/* Time Horizon */}
      <Box sx={{ bgcolor: bgColor, p: 3, borderRadius: 1 }}>
        <Typography variant="subtitle2" fontWeight="normal" sx={{ mb: 2 }}>
          {t(
            "dmSurvey.assessTimeHorizonOpportunity",
            "Assess the time horizon in which you expect the financial opportunity to occur:",
          )}
          <Tooltip
            title={t(
              "dmSurvey.assessTimeHorizonOpportunityTooltip",
              "Select the timeframe(s) when the financial opportunity is expected to be realized.",
            )}
          >
            <InfoOutlined
              sx={{
                fontSize: 18,
                color: "text.secondary",
                ml: 1,
                verticalAlign: "middle",
              }}
            />
          </Tooltip>
        </Typography>
        <FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                checked={currentAnswer.timeHorizon?.includes(0) || false}
                onChange={(e) => {
                  const current = currentAnswer.timeHorizon || [];

                  if (e.target.checked) {
                    onUpdate("timeHorizon", [...current, 0]);
                  } else {
                    onUpdate(
                      "timeHorizon",
                      current.filter((v) => v !== 0),
                    );
                  }
                }}
              />
            }
            label={
              <>{ranges.timeHorizon[0] || t("dmSurvey.shortTerm", "Short term")}</>
            }
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={currentAnswer.timeHorizon?.includes(1) || false}
                onChange={(e) => {
                  const current = currentAnswer.timeHorizon || [];

                  if (e.target.checked) {
                    onUpdate("timeHorizon", [...current, 1]);
                  } else {
                    onUpdate(
                      "timeHorizon",
                      current.filter((v) => v !== 1),
                    );
                  }
                }}
              />
            }
            label={
              <>{ranges.timeHorizon[1] || t("dmSurvey.mediumTerm", "Medium term")}</>
            }
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={currentAnswer.timeHorizon?.includes(2) || false}
                onChange={(e) => {
                  const current = currentAnswer.timeHorizon || [];

                  if (e.target.checked) {
                    onUpdate("timeHorizon", [...current, 2]);
                  } else {
                    onUpdate(
                      "timeHorizon",
                      current.filter((v) => v !== 2),
                    );
                  }
                }}
              />
            }
            label={
              <>{ranges.timeHorizon[2] || t("dmSurvey.longTerm", "Long term")}</>
            }
          />
        </FormGroup>
      </Box>
    </>
  );
};
